/**
 * `/admin` → **Features**: who gets which part of the app.
 *
 * The other tab on this page, Pages, is a display preference for *this browser*.
 * This one changes what everybody gets, so it is gated by `flags:read` /
 * `flags:write` on the server rather than by the access code, and it says so on
 * itself - the two sit next to each other and must not be confused.
 *
 * One row per feature, one control: an audience.
 *
 * * **By role** (Assistant and Argus only) - on for whoever holds the role that
 *   grants entry (AI Assistant User; an Argus role), except the exceptions.
 * * **Everyone** - on, except anyone in the exception list.
 * * **Admins only** - on for Administrator and Root, off for the rest.
 * * **People and groups** - off, except those named.
 * * **Off** - off for everyone, Administrator and Root included.
 *
 * Features takes precedence over roles: Off, the kill switch and an exception
 * block even Root, and an explicit audience admits people *into* a module
 * without its role - but never grants its actions (triage, authoring,
 * deploying), which stay with the roles. The preview at the top shows what one
 * person gets and why, from the same evaluator the API uses.
 *
 * Each answer is a canonical set of rows in `cfg.feature_flag_rule`, written by
 * the server (`flags_admin.set_audience`) so the translation exists once rather
 * than in both halves. Rules the picker could not have written - a percentage
 * rollout, an environment rule, anything hand-written in SQL - are reported by
 * the server as the `custom` audience: the picker then shows **Custom** and
 * refuses to summarise, and the rules are visible under Advanced where they can
 * be read and removed one at a time. A screen that quietly rewrote somebody's
 * rollout because it had no word for it would be worse than no screen.
 *
 * The list is re-read from the server's response after every change rather than
 * patched locally, for the reason the share dialog does the same: a change that
 * looks applied but was refused is the bug nobody notices in time.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  deleteFlagRule,
  getAccessExplanation,
  listAdminFlags,
  searchFlagPrincipals,
  setFlagActive,
  setFlagAudience,
} from '../../api/endpoints.ts';
import type { AccessExplanation, AdminFlag, FlagAudience, FlagRule, PrincipalSearch } from '../../api/types.ts';
import { loadMe } from '../../state/me.ts';
import {
  audienceAfterAdd,
  audienceAfterRemove,
  principalsOf,
  samePrincipal,
  type Choice,
  type PrincipalRef,
} from './flag-audience.ts';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, isRootUser, myPermissions } from '../../state/store.ts';
import { showToast } from '../../ui/toast.ts';
import { userMessage } from '../../api/errors.ts';

const AUDIENCE_LABEL: Record<FlagAudience, string> = {
  by_role: 'By role',
  everyone: 'Everyone',
  admins: 'Admins only',
  targeted: 'People and groups',
  off: 'Off',
  custom: 'Custom',
};

const AUDIENCE_HINT: Record<FlagAudience, string> = {
  by_role: 'On for people whose roles include it, directly or through a group. Exceptions still apply.',
  everyone: 'On for everyone signed in.',
  admins: 'On for Administrator and Root only - how an in-development feature is tried out.',
  targeted: 'Off, except the people and groups named below.',
  off: 'Off for everyone, Administrator and Root included.',
  custom: 'Rules this picker cannot describe. Open Advanced to see them.',
};

/** For features that need a role (Assistant, Argus, Dashboards): the audience narrows role holders. */
const ENTRY_HINT: Partial<Record<FlagAudience, string>> = {
  by_role: 'On for everyone whose roles include it, directly or through a group. Exceptions still apply.',
  everyone: 'On for everyone whose roles include it - the same as By role.',
  admins: 'On for Administrator and Root only.',
  targeted: 'On only for the people and groups named below who also hold the role.',
};

const CHEVRON = svg`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M8 5l8 7-8 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

const CLOSE = svg`<svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
</svg>`;

@customElement('feature-flags-panel')
export class FeatureFlagsPanel extends LightElement {
  private store = new StoreController(this, [myPermissions, isRootUser]);

  @state() private flags: AdminFlag[] = [];
  @state() private loading = true;
  @state() private error: string | null = null;
  /** Which feature's Advanced list is open. */
  @state() private expanded: string | null = null;
  /** Which feature's add-someone box is open, and whether it adds an exception. */
  @state() private picking: { key: string; deny: boolean } | null = null;
  @state() private query = '';
  @state() private found: PrincipalSearch = { users: [], groups: [] };
  /** The feature a request is in flight for, so its row can say so. */
  @state() private busyKey: string | null = null;
  /**
   * A feature switched to "People and groups" that has nobody named yet.
   *
   * The server refuses an empty audience - it would be "Off" wearing another
   * word - so nothing is written until the first name is picked. Until then the
   * row shows the audience the administrator chose rather than snapping back to
   * what the database still says, which would read as the click being ignored.
   */
  @state() private pendingTargeted: string | null = null;
  /** Whose access the rows preview, and what the server says they get. */
  @state() private previewQuery = '';
  @state() private previewFound: PrincipalSearch['users'] = [];
  @state() private preview: AccessExplanation | null = null;

  private searchTimer: number | null = null;
  private previewTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  /** After any change: the preview and this account's own flags are stale. */
  private refreshAfterChange(): void {
    if (this.preview) void this.previewUser(this.preview.user_id);
    void loadMe().catch(() => {});
  }

  private onPreviewSearch(value: string): void {
    this.previewQuery = value;
    if (this.previewTimer) clearTimeout(this.previewTimer);
    if (!value.trim()) {
      this.previewFound = [];
      return;
    }
    this.previewTimer = window.setTimeout(async () => {
      try {
        this.previewFound = (await searchFlagPrincipals(value.trim())).users;
      } catch {
        this.previewFound = [];
      }
    }, 200);
  }

  private async previewUser(id: string): Promise<void> {
    this.previewFound = [];
    this.previewQuery = '';
    try {
      this.preview = await getAccessExplanation(id);
    } catch (e) {
      showToast('error', userMessage(e, 'That preview could not be worked out.'));
    }
  }

  private renderPreviewBar(): TemplateResult {
    return html`<div class="ff-preview-bar">
      <span class="ff-chips-label">Preview</span>
      ${this.preview
        ? html`<span class="ff-chip">${this.preview.display_name}
            <button class="ff-chip-x" title="Stop previewing" aria-label="Stop previewing"
              @click=${() => { this.preview = null; }}>${CLOSE}</button></span>`
        : html`<div class="ff-picker ff-preview-picker">
            <input class="ff-search" type="search" placeholder="See what one person gets, and why"
              aria-label="Preview a person's access" .value=${this.previewQuery}
              @input=${(e: Event) => this.onPreviewSearch((e.target as HTMLInputElement).value)} />
            ${this.previewFound.length ? html`<ul class="ff-results">${this.previewFound.map((u) => html`<li>
              <button class="ff-result" @click=${() => void this.previewUser(u.id)}>
                <span class="ff-result-name">${u.display_name}</span>
                <span class="ff-result-kind">${u.email ?? 'person'}</span>
              </button></li>`)}</ul>` : nothing}
          </div>`}
    </div>`;
  }

  /** One line per row: what the previewed person gets from this feature, and the actions it gates. */
  private renderPreviewLine(flag: AdminFlag): TemplateResult | typeof nothing {
    const decision = this.preview?.features[flag.key];
    if (!this.preview || !decision) return nothing;
    const actions = this.preview.capabilities.filter((c) => c.feature === flag.key && c.permission && !c.entry);
    return html`<div class="ff-preview ${decision.allowed ? 'is-allowed' : 'is-blocked'}">
      <strong>${this.preview.display_name}: ${decision.allowed ? 'can use it' : 'cannot use it'}</strong>
      <span>${decision.reason}</span>
      ${actions.length ? html`<ul>${actions.map((c) => html`<li>${c.label}: ${c.allowed ? 'allowed' : 'blocked'} - ${c.reason}</li>`)}</ul>` : nothing}
    </div>`;
  }

  private get mayWrite(): boolean {
    return canDo('flags:write');
  }

  private async load(): Promise<void> {
    this.loading = true;
    try {
      const data = await listAdminFlags();
      this.flags = data.flags;
      this.error = null;
    } catch (e) {
      // 503 is the no-database answer and reads as an explanation, not a fault.
      this.error = userMessage(e, 'Could not read the feature flags.');
    } finally {
      this.loading = false;
    }
  }

  /** Replace one flag in place, from whatever the server just returned. */
  private absorb(updated: AdminFlag): void {
    this.flags = this.flags.map((f) => (f.key === updated.key ? updated : f));
  }

  private async apply(
    key: string,
    audience: Choice,
    principals: PrincipalRef[],
    exceptions: PrincipalRef[],
  ): Promise<void> {
    this.busyKey = key;
    try {
      const updated = await setFlagAudience(key, {
        audience,
        principals: principals.map(({ kind, id }) => ({ kind, id })),
        exceptions: exceptions.map(({ kind, id }) => ({ kind, id })),
      });
      this.absorb(updated);
      this.refreshAfterChange();
    } catch (e) {
      showToast('error', userMessage(e, 'That change was refused.'));
    } finally {
      this.busyKey = null;
    }
  }

  private onAudience(flag: AdminFlag, audience: Choice): void {
    if (audience === flag.audience) return;
    const allowed = principalsOf(flag, true);
    const denied = principalsOf(flag, false);
    // Moving to "People and groups" with nobody named yet would be refused by
    // the server, so open the picker instead of sending a request that fails.
    if (audience === 'targeted' && !allowed.length) {
      this.pendingTargeted = flag.key;
      this.picking = { key: flag.key, deny: false };
      this.query = '';
      this.found = { users: [], groups: [] };
      return;
    }
    this.pendingTargeted = null;
    void this.apply(flag.key, audience, allowed, audience === 'off' ? [] : denied);
  }

  private async onToggleActive(flag: AdminFlag): Promise<void> {
    this.busyKey = flag.key;
    try {
      this.absorb(await setFlagActive(flag.key, !flag.is_active));
      this.refreshAfterChange();
    } catch (e) {
      showToast('error', userMessage(e, 'That change was refused.'));
    } finally {
      this.busyKey = null;
    }
  }

  private async onDeleteRule(flag: AdminFlag, rule: FlagRule): Promise<void> {
    this.busyKey = flag.key;
    try {
      this.absorb(await deleteFlagRule(flag.key, rule.id));
      this.refreshAfterChange();
    } catch (e) {
      showToast('error', userMessage(e, 'That rule could not be removed.'));
    } finally {
      this.busyKey = null;
    }
  }

  private onSearch(value: string): void {
    this.query = value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (!value.trim()) {
      this.found = { users: [], groups: [] };
      return;
    }
    this.searchTimer = window.setTimeout(async () => {
      try {
        this.found = await searchFlagPrincipals(value.trim());
      } catch {
        this.found = { users: [], groups: [] };
      }
    }, 200);
  }

  private add(flag: AdminFlag, principal: PrincipalRef, deny: boolean): void {
    const allowed = principalsOf(flag, true);
    const denied = principalsOf(flag, false);
    const list = deny ? denied : allowed;
    if (list.some((p) => samePrincipal(p, principal))) return;
    list.push(principal);
    const audience = audienceAfterAdd(flag.audience, deny);
    this.picking = null;
    this.pendingTargeted = null;
    this.query = '';
    void this.apply(flag.key, audience, allowed, denied);
  }

  /** Take one name off a flag's allow list, or off its exception list. */
  private dropPrincipal(flag: AdminFlag, principal: PrincipalRef, deny: boolean): void {
    const keep = (p: PrincipalRef): boolean => !samePrincipal(p, principal);
    const allowed = deny ? principalsOf(flag, true) : principalsOf(flag, true).filter(keep);
    const denied = deny ? principalsOf(flag, false).filter(keep) : principalsOf(flag, false);
    void this.apply(flag.key, audienceAfterRemove(flag.audience, allowed.length), allowed, denied);
  }

  private renderPicker(flag: AdminFlag): TemplateResult {
    const deny = this.picking?.deny ?? false;
    const people = this.found.users.map<PrincipalRef>((u) => ({
      kind: 'user',
      id: u.id,
      name: u.display_name,
    }));
    const groups = this.found.groups.map<PrincipalRef>((g) => ({
      kind: 'group',
      id: g.id,
      name: g.name,
    }));
    const all = [...groups, ...people];
    return html`
      <div class="ff-picker">
        <input
          class="ff-search"
          type="search"
          placeholder=${deny ? 'Who should not get it?' : 'Who should get it?'}
          .value=${this.query}
          autofocus
          aria-label=${deny ? 'Search for an exception' : 'Search for a person or group'}
          @input=${(e: Event) => this.onSearch((e.target as HTMLInputElement).value)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Escape') this.picking = null;
          }}
        />
        ${all.length
          ? html`<ul class="ff-results">
              ${all.map(
                (p) => html`<li>
                  <button class="ff-result" @click=${() => this.add(flag, p, deny)}>
                    <span class="ff-result-name">${p.name}</span>
                    <span class="ff-result-kind">${p.kind === 'group' ? 'group' : 'person'}</span>
                  </button>
                </li>`,
              )}
            </ul>`
          : this.query.trim()
            ? html`<p class="ff-none">Nobody matches “${this.query}”</p>`
            : nothing}
      </div>
    `;
  }

  private renderChips(flag: AdminFlag, principals: PrincipalRef[], deny: boolean): TemplateResult {
    const picking = this.picking?.key === flag.key && this.picking.deny === deny;
    // Nothing named and nothing open: the line is only an offer, so it fades in
    // on hover or focus rather than sitting on every row (see `admin.css`).
    const empty = !principals.length && !picking;
    return html`
      <div class="ff-chips ${empty ? 'is-empty' : ''}">
        <span class="ff-chips-label">${deny ? 'Except' : 'Who'}</span>
        ${principals.map(
          (p) => html`<span class="ff-chip ${p.kind === 'group' ? 'is-group' : ''}">
            ${p.name}
            ${this.mayWrite
              ? html`<button
                  class="ff-chip-x"
                  title="Remove ${p.name}"
                  aria-label="Remove ${p.name}"
                  @click=${() => this.dropPrincipal(flag, p, deny)}
                >
                  ${CLOSE}
                </button>`
              : nothing}
          </span>`,
        )}
        ${this.mayWrite
          ? html`<button
              class="ff-add"
              @click=${() => {
                this.picking = picking ? null : { key: flag.key, deny };
                this.query = '';
                this.found = { users: [], groups: [] };
              }}
            >
              ${picking ? 'Cancel' : deny ? '+ exception' : '+ add'}
            </button>`
          : nothing}
        ${picking ? this.renderPicker(flag) : nothing}
      </div>
    `;
  }

  private renderAdvanced(flag: AdminFlag): TemplateResult {
    if (!flag.rules.length) {
      return html`<p class="ff-advanced-empty">
        No rules. Everyone gets the default, which is
        <strong>${flag.default_value === true ? 'on' : 'off'}</strong>.
      </p>`;
    }
    return html`
      <ol class="ff-rules">
        ${flag.rules.map(
          (rule) => html`<li class="ff-rule ${rule.enabled ? '' : 'is-disabled'}">
            <span class="ff-rule-priority" title="Priority - lowest wins">${rule.priority}</span>
            <span class="ff-rule-label">${rule.label}</span>
            ${this.mayWrite
              ? html`<button
                  class="ff-rule-x"
                  title="Remove this rule"
                  aria-label="Remove rule ${rule.priority}"
                  @click=${() => this.onDeleteRule(flag, rule)}
                >
                  ${CLOSE}
                </button>`
              : nothing}
          </li>`,
        )}
      </ol>
      <p class="ff-advanced-note">
        Read in order, first match wins; anything unmatched falls back to the default
        (<strong>${flag.default_value === true ? 'on' : 'off'}</strong>). Percentage and
        environment rules are written in SQL - see <code>docs/plans/postgresql.md</code>.
      </p>
    `;
  }

  private renderRow(flag: AdminFlag): TemplateResult {
    const pending = this.pendingTargeted === flag.key;
    const audience: FlagAudience = pending ? 'targeted' : flag.audience;
    const busy = this.busyKey === flag.key;
    const open = this.expanded === flag.key;
    const allowed = principalsOf(flag, true);
    const denied = principalsOf(flag, false);
    const choices: Choice[] = flag.entry_permission
      ? ['by_role', 'everyone', 'admins', 'targeted', 'off']
      : ['everyone', 'admins', 'targeted', 'off'];
    return html`
      <div class="ff-row ${busy ? 'is-busy' : ''} ${flag.is_active ? '' : 'is-killed'}">
        <div class="ff-main">
          <div class="ff-text">
            <span class="ff-name">
              ${flag.name}
              <code class="ff-key">${flag.key}</code>
              ${flag.is_active
                ? nothing
                : html`<span class="ff-killed-tag" title="Every rule is ignored: off for everyone, Administrator and Root included">switched off</span>`}
            </span>
            <span class="ff-desc">${flag.description ?? AUDIENCE_HINT[audience]}</span>
          </div>
          <select
            class="ff-audience"
            aria-label="Who gets ${flag.name}"
            .value=${audience}
            ?disabled=${!this.mayWrite || busy || !flag.is_active}
            @change=${(e: Event) =>
              this.onAudience(flag, (e.target as HTMLSelectElement).value as Choice)}
          >
            ${audience === 'custom'
              ? html`<option value="custom">${AUDIENCE_LABEL.custom}</option>`
              : nothing}
            ${choices.map(
              (c) => html`<option value=${c} ?selected=${c === audience}>
                ${AUDIENCE_LABEL[c]}
              </option>`,
            )}
          </select>
        </div>

        <p class="ff-hint">${flag.entry_permission && ENTRY_HINT[audience] ? ENTRY_HINT[audience] : AUDIENCE_HINT[audience]}${flag.entry_permission
          ? html` <span class="ff-entry-note">Only people with the role can use it; the audience narrows who among them. Without the role it is struck through in their navigation.</span>`
          : nothing}</p>
        ${this.renderPreviewLine(flag)}

        ${audience === 'targeted' || (audience === 'custom' && allowed.length)
          ? this.renderChips(flag, allowed, false)
          : nothing}
        ${pending && !allowed.length
          ? html`<p class="ff-hint ff-pending">
              Nobody named yet - nothing is switched on until you add someone.
            </p>`
          : nothing}
        ${audience === 'everyone' || audience === 'admins' || audience === 'by_role' || denied.length
          ? this.renderChips(flag, denied, true)
          : nothing}

        <div class="ff-row-foot">
          <button
            class="ff-advanced-toggle ${open ? 'is-open' : ''}"
            aria-expanded=${open}
            @click=${() => {
              this.expanded = open ? null : flag.key;
            }}
          >
            ${CHEVRON} Advanced
            <span class="ff-rule-count">${flag.rules.length}</span>
          </button>
          ${this.mayWrite
            ? html`<label class="ff-kill">
                <span>Feature enabled</span>
                <input
                  class="admin-switch"
                  type="checkbox"
                  role="switch"
                  .checked=${flag.is_active}
                  ?disabled=${busy}
                  aria-label="Enable ${flag.name} at all"
                  @change=${() => this.onToggleActive(flag)}
                />
              </label>`
            : nothing}
        </div>

        ${open ? html`<div class="ff-advanced">${this.renderAdvanced(flag)}</div>` : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    if (!canDo('flags:read')) {
      return html`<p class="ff-empty">
        Changing features needs the <code>flags:read</code> permission.
      </p>`;
    }
    if (this.loading) return html`<p class="ff-empty">Reading the feature flags…</p>`;
    if (this.error) return html`<p class="ff-empty">${this.error}</p>`;
    if (!this.flags.length) {
      return html`<p class="ff-empty">This deployment has no feature flags stored.</p>`;
    }
    return html`
      ${this.renderPreviewBar()}
      <div class="ff-list">${this.flags.map((flag) => this.renderRow(flag))}</div>
      <p class="admin-note ff-scope">
        These are stored in the database and apply to <strong>everyone</strong>, not just this
        browser${this.mayWrite
          ? nothing
          : html` — and you may look but not change them, which needs
              <code>flags:write</code>`}. A feature switched off here is refused by its API as
        well as hidden in the navigation - for Administrator and Root too. This screen is
        never switched off, so a decision made here can always be reversed here.
      </p>
    `;
  }
}
